/**

------------------------------------------------------------------------------------------------------------------------------------------
THEME ENTRY POINT
------------------------------------------------------------------------------------------------------------------------------------------

This entry point is used for the theme itself, specifically on the front-end, and should provide all the logic and styling for components
or common elements such as the headers, footers and menu.

*/

import { Components } from '../components';
import header from '../common/header';
import footer from '../common/footer';
import { SITE_NAME, siteConfig } from './../common/common';
import './../styles/common.scss';
import './../styles/theme.scss';
import './../styles/index.scss';
import { barbaController } from '../common/page-lifecycle/barba/BarbaController';
import { PageEvent } from '../common/page-lifecycle/PageEvent';
import { scrollController } from '../common/ScrollController';
import { forceFocusReset } from '../components/ComponentUtils';

import menu, { MenuCloseReason, MenuState } from '../common/menu';
import { updateWeglotDropdownUrls } from '../utils/update-weglot-dropdown-urls';

import { CSSBreakpoint } from '../common/constants-theme';

Components.register(true, 'theme.ts');

logger.info('Theme loaded for ' + SITE_NAME);

document.addEventListener('DOMContentLoaded', () => {
	document.body.classList.add('loaded');

	header.init();
	footer.init();

	a11yInit();

	const adminBar = document.querySelector('#wpadminbar');
	if (!adminBar && siteConfig.enablePageTransitions) {
		barbaController.init({ scrollToTopOnTransition: true });
	}

	if (siteConfig.enableRevealOnScroll) {
		scrollController.init({ revealOnScroll: siteConfig.enableRevealOnScroll });
	}

	document.addEventListener(PageEvent.enter, (event: any) => {
		if (event?.detail?.next) updateWeglotDropdownUrls(event.detail.next.html);
	});

	// Manually dispatch PageEvents that will be handled by a page-transition system
	// beyond the initial page load. This ensures that the sequencing of components,
	// menus etc. that use the page-lifecycle system is consistent.
	document.dispatchEvent(new CustomEvent(PageEvent.enter));
	document.dispatchEvent(new CustomEvent(PageEvent.enterComplete));

	initResizeWatcher();
});

window.addEventListener('load', () => {
	// Set scroll restoration to manual to avoid scroll on soft-reload of previous pages.
	// This must be done after the load event to avoid the browser resetting it.
	history.scrollRestoration = 'manual';

	//ScrollTrigger.addEventListener('refresh', () => console.log('ScrollTrigger REFRESH'));
	//window.setInterval(() => console.log('scrollRestoration', history.scrollRestoration), 20);

	// Debug code to test page transitions, as setting href manually doesn't trigger.
	/*window.addEventListener('keydown', (e: KeyboardEvent) => {
		if (e.key === 'g') {
			const target = document.querySelector('a[href="https://mohala.local/melia/"]') as HTMLAnchorElement;
			target?.click();
		}
	});*/
});

function a11yInit() {
	// @a11y - Scroll focused elements into view

	let wasInMenu = false;

	const Offset = 100; // Enough room for the header and form element with a label

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Tab') {
			requestAnimationFrame(() => {
				if (!document.activeElement) return;

				const activeEl = document.activeElement as HTMLElement;

				const isInMenu = !!activeEl.closest('nav.menu');

				/**
				 * If the menu is open after focus has left a child element then we close the menu
				 */

				if (wasInMenu === true && isInMenu === false && menu.state !== MenuState.Closed) menu.close(MenuCloseReason.UserInteraction);

				wasInMenu = isInMenu;

				/**
				 * If we have a focussed element that's inside the menu then we don't
				 * want any scrolling to happen so abort here.
				 */

				if (isInMenu) return;

				// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

				// Check if inside footer
				const isInFooter = !!activeEl.closest('footer');

				// Check for the closest HHDS parent
				const hhdsParentEl = findUltimateHHDSParent(activeEl);

				/**
				 * Determine the target to scroll into view.
				 * - If it's in the footer, we could decide to handle this differently (optional — scroll footer into view).
				 * - Otherwise, use HHDS parent if it's reasonably sized.
				 */

				let scrollToTargetEl: HTMLElement = activeEl;

				if (isInFooter) {
					// Optionally, scroll the entire footer into view if desired.
					// This example keeps the element as target (fine for most cases).

					const footerEl = document.querySelector('footer');

					if (footerEl) scrollToTargetEl = footerEl;
				} else if (hhdsParentEl && hhdsParentEl.offsetHeight < window.innerHeight) {
					scrollToTargetEl = hhdsParentEl; // Use parent if it's within viewport height
				}

				// Get the target's position
				const boundingClientRect = scrollToTargetEl.getBoundingClientRect();
				const absoluteY = window.scrollY + boundingClientRect.top;

				const top = absoluteY - Offset;
				const behavior = 'smooth';

				// console.log('TabHandler', { absoluteY, top, boundingClientRect, isInFooter });

				window.scrollTo({
					top,
					behavior,
				});
			});
		}
	});

	// @debug
	// window.addEventListener('scroll', () => {
	// 	console.log('scroll', window.scrollY);
	// });

	/**
	 * Reset the focus when a new page has loaded as tabbing does not continue
	 * from the last element that had focus.
	 */

	document.addEventListener(PageEvent.enterComplete, () => {
		forceFocusReset();
	});

	// ////////////////////////////////////////////////////////////////////
	/**
	 * a11y - Tapping or clicking will apply a `using-mouse` class which disables
	 * elemnt focus outline. Using Tab restores default outline.
	 * Fixes DoneDone #82
	 */

	// Let the document know when the mouse is being used
	document.addEventListener('mousedown', () => {
		document.body.classList.toggle('using-mouse', true);
	});

	// Re-enable focus styling when Tab is pressed
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Tab') {
			document.body.classList.toggle('using-mouse', false);
		}
	});
}

function findUltimateHHDSParent(element: HTMLElement): HTMLElement | null {
	let ultimateParent: HTMLElement | null = null;
	let current = element.parentElement;

	while (current) {
		if (current.tagName.toLowerCase().startsWith('hhds-')) {
			ultimateParent = current; // Found a new HHDS ancestor — keep track of it
		}
		current = current.parentElement;
	}

	return ultimateParent; // This will be the outermost (farthest up) hhds- element found
}

// ////////////////////////////////////////////////////////////////////

function initResizeWatcher() {
	const containerMenuBurgerEl = document.querySelector('[ref="container-menu-burger"]') as HTMLElement;
	const containerMenuEl = document.querySelector('[ref="container-menu-horizontal"]') as HTMLElement;

	if (!containerMenuBurgerEl || !containerMenuEl) throw new Error('Unable to derive containers!');

	const updateContainers = () => {
		if (window.matchMedia(`(min-width: ${CSSBreakpoint.Desktop}px)`).matches) {
			containerMenuBurgerEl.style.display = 'none';
			containerMenuBurgerEl.setAttribute('aria-hidden', 'true');

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			containerMenuEl.style.display = 'block';
			containerMenuEl.removeAttribute('aria-hidden');
		} else {
			containerMenuBurgerEl.style.display = 'block';
			containerMenuBurgerEl.removeAttribute('aria-hidden');

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			containerMenuEl.style.display = 'none';
			containerMenuEl.setAttribute('aria-hidden', 'true');
		}
	};

	const resizeObserver = new ResizeObserver(() => {
		updateContainers();
	});

	resizeObserver.observe(document.documentElement); // Observe body resizing as a proxy for viewport changes

	// Initial check
	updateContainers();
}
